import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useThemeStore } from '@/stores/themeStore';
import { useAuthSubscription } from '@/hooks/useAuth';
import { PublicShell } from '@/components/layout/PublicShell';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { AdminShell } from '@/components/layout/AdminShell';
import { ShopShell } from '@/components/layout/ShopShell';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { AdminRoute } from '@/routes/AdminRoute';

const Landing = lazy(() => import('@/pages/public/Landing'));
const Login = lazy(() => import('@/pages/public/Login'));
const Signup = lazy(() => import('@/pages/public/Signup'));
const ResetPassword = lazy(() => import('@/pages/public/ResetPassword'));
const Pricing = lazy(() => import('@/pages/public/Pricing'));
const Overview = lazy(() => import('@/pages/dashboard/Overview'));
const Products = lazy(() => import('@/pages/dashboard/Products'));
const Orders = lazy(() => import('@/pages/dashboard/Orders'));
const OrderDetail = lazy(() => import('@/pages/dashboard/OrderDetail'));
const Profile = lazy(() => import('@/pages/dashboard/Profile'));
const Share = lazy(() => import('@/pages/dashboard/Share'));
const Billing = lazy(() => import('@/pages/dashboard/Billing'));
const Settings = lazy(() => import('@/pages/dashboard/Settings'));
const Storefront = lazy(() => import('@/pages/shop/Storefront'));
const Checkout = lazy(() => import('@/pages/shop/Checkout'));
const Confirmation = lazy(() => import('@/pages/shop/Confirmation'));
const Track = lazy(() => import('@/pages/shop/Track'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminMerchantDetail = lazy(() => import('@/pages/admin/AdminMerchantDetail'));
const NotFound = lazy(() => import('@/pages/NotFound'));

const RouteFallback = () => (
  <div className="grid min-h-[50vh] place-items-center text-muted">…</div>
);

export default function App() {
  const apply = useThemeStore((s) => s.apply);
  useAuthSubscription();

  useEffect(() => {
    apply();
  }, [apply]);

  return (
    <BrowserRouter>
      <Toaster richColors closeButton position="top-right" />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<PublicShell />}>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Pricing />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardShell />}>
              <Route path="/dashboard" element={<Overview />} />
              <Route path="/dashboard/products" element={<Products />} />
              <Route path="/dashboard/orders" element={<Orders />} />
              <Route path="/dashboard/orders/:orderId" element={<OrderDetail />} />
              <Route path="/dashboard/profile" element={<Profile />} />
              <Route path="/dashboard/share" element={<Share />} />
              <Route path="/dashboard/billing" element={<Billing />} />
              <Route path="/dashboard/settings" element={<Settings />} />
            </Route>
          </Route>

          <Route element={<AdminRoute />}>
            <Route element={<AdminShell />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/:ownerId" element={<AdminMerchantDetail />} />
            </Route>
          </Route>

          <Route element={<ShopShell />}>
            <Route path="/:slug" element={<Storefront />} />
            <Route path="/:slug/checkout" element={<Checkout />} />
            <Route path="/:slug/confirmation" element={<Confirmation />} />
            <Route path="/:slug/track/:orderId" element={<Track />} />
          </Route>

          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
